import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { formatDateTimeLocal } from '../utils/formatDate';

import { NotificationApiProvider, useNotificationApi } from '../Services/NotificationApiProvider';
import useTablePagination from '../Hooks/useTablePagination';

function NotificationsHub({ canEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getNotifications, deleteNotification } = useNotificationApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [alertShown, setAlertShown] = useState(false);

  const tableTitle = 'Notifications';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
      console.log('notifications:', notifications);
    }
    if (location.state?.returnMessage && !alertShown) {
      showAlert('help', location.state.returnMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [
    location.state?.successMessage,
    alertShown,
    navigate,
    location.pathname,
    showAlert,
    notifications,
  ]);

  const notificationTypes = {
    TELEGRAM: 'Telegram',
    EMAIL: 'Email',
  };

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: true },
    { name: 'Name', selector: (row) => row.name, sortable: true },
    { name: 'Type', selector: (row) => notificationTypes[row.notification_type], sortable: true },
    {
      name: 'Status',
      selector: (row) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: true,
    },
    { name: 'Created', selector: (row) => formatDateTimeLocal(row.created_at), sortable: true },
    { name: 'Updated', selector: (row) => formatDateTimeLocal(row.updated_at), sortable: true },
  ];

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/notifications-hub/${row.id}`, {
      state: { organizationId },
    });
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;
    showConfirmation(`Are you sure you want to delete the selected notification?`, async () => {
      try {
        setLoading(true);
        await deleteNotification(selectedId);
        fetchNotifications(storedSkip(), storedLimit());
        showAlert('success', 'Notification was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the notification!');
      }
    });
  };

  const fetchNotifications = async (skip, limit) => {
    try {
      setLoading(true);
      const response = await getNotifications(skip, limit);
      setNotifications(response.items);
      setTotalNotifications(response.totalCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(storedSkip(), storedLimit());
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={notifications}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={initialPageSize}
                paginationTotalRows={totalNotifications}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                onChangePage={fetchNotifications}
                getRowUrl={(row) => `/organizations/${organizationId}/notifications-hub/${row.id}`}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/notifications-hub/new-notification`)
                  }
                >
                  Create New Notification
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationsHubWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <NotificationApiProvider>
        <NotificationsHub canEdit={canEdit} />
      </NotificationApiProvider>
    </ConfirmationProvider>
  );
}

export default NotificationsHubWrapper;
